import { useEffect, useState } from "react";
import { ArrowRight, CalendarDays, Cake, ClipboardCheck, ListChecks, UserPlus, Users, Activity } from "lucide-react";
import { rpc } from "../../services/datos";
import { botonPequeno, botonPrimario, fechaLarga, hora } from "../../shared/estilos";

const ACCION_PENDIENTE = {
  rechazo: ["servidores", "Reemplazar"],
  servidores: ["servidores", "Programar"],
  confirmaciones: ["servidores", "Ver turnos"],
  programa: ["programacion", "Preparar"],
  sin_reuniones: ["reuniones", "Calendario"],
};

function saludo() {
  const horaActual = new Date().getHours();
  if (horaActual < 12) return "Buenos días";
  if (horaActual < 19) return "Buenas tardes";
  return "Buenas noches";
}

// Cifra del resumen: toda la tarjeta lleva a la sección (más fácil de tocar en el celular).
function Cifra({ titulo, valor, detalle, onClick, accion, Icono }) {
  return (
    <button type="button" onClick={onClick} className="panel group flex flex-col p-5 text-left">
      <span className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">{titulo}</span>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/5 text-gray-300 transition group-hover:bg-white group-hover:text-black">
          <Icono size={16} aria-hidden="true" />
        </span>
      </span>
      <span className="mt-3 text-4xl font-black tracking-tight text-white">{valor ?? "—"}</span>
      {detalle && <span className="mt-1 text-sm text-gray-400">{detalle}</span>}
      <span className="mt-4 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 transition group-hover:text-white">
        {accion} <ArrowRight size={12} aria-hidden="true" className="transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

function TituloSeccion({ Icono, children }) {
  return (
    <h3 className="titulo-panel flex items-center gap-2">
      <Icono size={15} aria-hidden="true" className="text-gray-500" />
      {children}
    </h3>
  );
}

// Inicio: lo que el pastor necesita saber y hacer, en el orden en que lo necesita.
function Dashboard({ irA, usuario }) {
  const [resumen, setResumen] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    rpc("resumen_inicio").then(setResumen).catch((e) => setError(e.message || "No se pudo cargar el resumen."));
  }, []);

  const hoy = new Date();
  const nombre = usuario?.miembro?.nombres;
  const encabezado = (
    <div className="mb-8">
      <h2 className="titulo-pagina">{nombre ? `${saludo()}, ${nombre}` : "Bienvenido"}</h2>
      <p className="mt-2 capitalize text-gray-400">
        {hoy.toLocaleDateString("es-CO", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
      </p>
    </div>
  );

  if (error) {
    return <div className="p-6">{encabezado}<p role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-200">{error}</p></div>;
  }
  if (!resumen) {
    return (
      <div className="p-6">
        {encabezado}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" aria-label="Cargando resumen...">
          <div className="panel esqueleto h-64 xl:col-span-2" />
          <div className="panel esqueleto h-64" />
        </div>
      </div>
    );
  }

  const { proxima_reunion: proxima, personas, asistencia, cumpleanos, pendientes } = resumen;
  const conDatos = asistencia.filter((a) => a.presentes !== null);
  const ultima = conDatos[conDatos.length - 1];
  const promedio = conDatos.length > 1
    ? Math.round(conDatos.slice(0, -1).reduce((suma, a) => suma + a.presentes, 0) / (conDatos.length - 1))
    : null;
  const maximo = Math.max(1, ...conDatos.map((a) => a.presentes));
  const diaProxima = proxima && new Date(`${proxima.fecha}T00:00:00`);

  return (
    <div className="p-6">
      {encabezado}

      <div className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="panel relative overflow-hidden border-white/20 p-6 sm:p-8 xl:col-span-2">
          {/* Brillo suave en la esquina: la tarjeta principal del Inicio. */}
          <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
          <TituloSeccion Icono={CalendarDays}>Próxima reunión</TituloSeccion>
          {!proxima ? (
            <div className="mt-4">
              <p className="text-gray-300">No hay reuniones programadas.</p>
              <button onClick={() => irA("reuniones")} className={`${botonPrimario} mt-4`}>Ir a Calendario</button>
            </div>
          ) : (
            <>
              <div className="mt-5 flex items-center gap-5">
                <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-2xl bg-white text-black">
                  <span className="text-3xl font-black leading-none">{diaProxima.getDate()}</span>
                  <span className="mt-1 text-[10px] font-bold uppercase tracking-widest">
                    {diaProxima.toLocaleDateString("es-CO", { month: "short" }).replace(".", "")}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">{proxima.nombre}</p>
                  <p className="mt-1 capitalize text-gray-400">{fechaLarga(proxima.fecha)}{proxima.hora ? ` · ${hora(proxima.hora)}` : ""}</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Programados", proxima.servidores.total, "text-white"],
                  ["Confirmados", proxima.servidores.confirmados, "text-emerald-300"],
                  ["Sin responder", proxima.servidores.pendientes, "text-amber-300"],
                  ["No pueden", proxima.servidores.rechazados, "text-rose-300"],
                ].map(([texto, valor, color]) => (
                  <div key={texto} className="panel-interior p-4">
                    <p className={`text-2xl font-black ${color}`}>{valor}</p>
                    <p className="mt-1 text-xs text-gray-400">{texto}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-400">
                Programa: {proxima.programa.listo
                  ? <span className="text-white">listo · {proxima.programa.canciones} canción(es)</span>
                  : <span className="text-amber-300">falta la prédica y las canciones</span>}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={() => irA("servidores", { servicioId: proxima.id })} className={botonPequeno}>Servidores</button>
                <button onClick={() => irA("programacion", { servicioId: proxima.id })} className={botonPequeno}>Programa</button>
                <button onClick={() => irA("reuniones")} className={botonPequeno}>Calendario</button>
              </div>
              {resumen.reuniones_proximas.length > 0 && (
                <p className="mt-6 border-t border-white/10 pt-4 text-xs text-gray-500">
                  Después: {resumen.reuniones_proximas.map((r) => `${fechaLarga(r.fecha, { weekday: "short", day: "numeric" })} ${r.nombre}`).join(" · ")}
                </p>
              )}
            </>
          )}
        </section>

        <section className="panel p-6">
          <TituloSeccion Icono={ListChecks}>Pendientes</TituloSeccion>
          {pendientes.length === 0 ? (
            <p className="mt-4 text-sm text-gray-300">Todo listo para la próxima reunión.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {pendientes.map((p, i) => {
                const [destino, accion] = ACCION_PENDIENTE[p.tipo] || [];
                return (
                  <li key={i} className="panel-interior border-l-2 border-l-amber-400/60 p-3">
                    <p className="text-sm text-gray-200">{p.texto}</p>
                    {destino && (
                      <button onClick={() => irA(destino, proxima ? { servicioId: proxima.id } : {})} className={`${botonPequeno} mt-2`}>
                        {accion}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Cifra titulo="Asistencia última reunión" valor={ultima?.presentes} Icono={ClipboardCheck}
          detalle={ultima ? (promedio !== null ? `Promedio anterior: ${promedio}` : ultima.nombre) : "Aún no se ha tomado asistencia"}
          onClick={() => irA("asistencia")} accion="Tomar asistencia" />
        <Cifra titulo="Miembros activos" valor={personas.miembros_activos} Icono={Users}
          detalle={`${personas.servidores ?? 0} servidor(es) · ${personas.congregantes ?? 0} congregante(s) · ${personas.visitantes} visitante(s)`}
          onClick={() => irA("miembros")} accion="Ver miembros" />
        <Cifra titulo="Visitas este mes" valor={personas.visitas_mes} Icono={UserPlus}
          detalle={`${personas.visitas_sin_seguimiento} sin seguimiento`} onClick={() => irA("visitas_nuevas")} accion="Ver visitas" />
        <Cifra titulo="Necesitan seguimiento" valor={personas.necesitan_seguimiento} Icono={Activity} detalle="4 semanas o más sin venir"
          onClick={() => irA("permanencia")} accion="Ver lista" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <section className="panel p-6 xl:col-span-2">
          <TituloSeccion Icono={ClipboardCheck}>Asistencia de las últimas reuniones</TituloSeccion>
          {asistencia.length === 0 ? (
            <p className="mt-4 text-sm text-gray-400">Aún no hay reuniones realizadas.</p>
          ) : (
            <ul className="mt-5 space-y-3">
              {asistencia.map((a, i) => (
                <li key={a.servicio_id} className="grid grid-cols-[5.5rem_1fr_2.5rem] items-center gap-3 text-sm sm:grid-cols-[7rem_1fr_3rem]">
                  <span className="truncate text-gray-400">{fechaLarga(a.fecha, { day: "numeric", month: "short" })}</span>
                  <span className="h-2 overflow-hidden rounded-full bg-white/5" title={a.nombre}>
                    {a.presentes !== null && (
                      <span
                        className="block h-full origin-left animate-crecer rounded-full bg-gradient-to-r from-white/60 to-white"
                        style={{ width: `${(a.presentes / maximo) * 100}%`, animationDelay: `${i * 60}ms` }}
                      />
                    )}
                  </span>
                  <span className={`text-right font-bold ${a.presentes === null ? "text-gray-600" : "text-white"}`}>
                    {a.presentes ?? "—"}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-4 text-xs text-gray-600">— significa que en esa reunión no se tomó asistencia.</p>
        </section>

        <section className="panel p-6">
          <TituloSeccion Icono={Cake}>Cumpleaños esta semana</TituloSeccion>
          {cumpleanos.length === 0 ? (
            <p className="mt-4 text-sm text-gray-400">Nadie cumple años en los próximos 7 días.</p>
          ) : (
            <ul className="mt-4 divide-y divide-white/5">
              {cumpleanos.map((m) => (
                <li key={m.id} className="flex justify-between gap-3 py-2.5 text-sm">
                  <span className="text-white">{m.nombres} {m.apellidos}</span>
                  <span className="capitalize text-gray-400">{fechaLarga(m.fecha, { weekday: "short", day: "numeric" })}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

export default Dashboard;
